/**
 * Production's pre-TRUS-762 version, kept verbatim for orgs WITHOUT the
 * `member_profile_v2` capability (they keep the previous member profile).
 * Do not add features here; remove once every org is on member_profile_v2.
 */
/**
 * Member Activity feed — the readable notes + unified timeline screen (TRUS-420).
 *
 * Replaces the Activity Log table, whose Notes column was `max-w-xs truncate`, so
 * a DCM could never actually read a note on the page.
 *
 * What this renders:
 *   - every note on the member, newest first, in FULL, in one scrollable list
 *   - author, timestamp and tags on each note
 *   - member/workflow/activity updates in the same list, visually distinct from
 *     notes a DCM wrote (left rail colour + icon + a muted card)
 *   - a category filter, and a composer that tags from the org's own list
 *
 * Note ordering matters in this file: every useState is declared before the
 * hooks that read it. Reading a `const` above its own declaration is a temporal
 * dead zone ReferenceError that renders the whole page blank in the production
 * build only — that is TRUS-418, four days of dead public forms.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  FileText, Filter, Loader2, Plus, Save, Settings2, Tag, Trash2, User, X,
  GitBranch, ClipboardList, PencilLine, Paperclip, Download,
} from 'lucide-react';

import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  memberActivityFeedAPI,
  type ActivityFeedItem,
  type NoteCategory,
  type FeedOrigin,
  type NoteAttachment,
} from '@/services/api/memberActivityFeed';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { useFormattedDate } from '../../hooks/useUserTimezone';
import { getApiErrorMessage } from '@/utils/apiErrors';
import { enrollmentsAPI } from '@/services/api/enrollments';

interface MemberActivityFeedProps {
  memberId: number;
  /** Attaches new notes to a specific enrollment; otherwise the API picks the member's current one. */
  enrollmentId?: number;
  readOnly?: boolean;
}

const KIND_STYLE: Record<string, { icon: React.ElementType; rail: string; label: string }> = {
  note:          { icon: FileText,      rail: 'bg-v2accent',   label: 'Note' },
  member_update: { icon: PencilLine,    rail: 'bg-sky-500',    label: 'Member update' },
  workflow:      { icon: GitBranch,     rail: 'bg-violet-500', label: 'Workflow' },
  activity:      { icon: ClipboardList, rail: 'bg-amber-500',  label: 'Activity' },
};

const PAGE_SIZE = 50;
// Matches the API's Query(le=200) ceiling; asking beyond it is a 422.
const MAX_FEED_LIMIT = 200;

/** Feed ids are namespaced ("activity:42") so they cannot collide across sources. */
function activityIdOf(item: ActivityFeedItem): number | null {
  const [kind, id] = (item.id || '').split(':');
  if (kind !== 'activity') return null;
  const n = Number(id);
  return Number.isFinite(n) ? n : null;
}

export default function MemberActivityFeed({
  memberId,
  enrollmentId,
  readOnly = false,
}: MemberActivityFeedProps) {
  const { t } = useAppTranslation();
  const { fmt } = useFormattedDate();

  // --- state (all declared before any hook that reads it) -------------------
  const [items, setItems] = useState<ActivityFeedItem[]>([]);
  const [categories, setCategories] = useState<NoteCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<number | null>(null);
  const [originFilter, setOriginFilter] = useState<FeedOrigin | null>(null);
  const [limit, setLimit] = useState(PAGE_SIZE);

  const [composerOpen, setComposerOpen] = useState(false);
  // Files chosen in the composer. The note has to exist before anything can hang off
  // it, so these are held here and uploaded immediately after the note is created
  // (TRUS-520).
  const [draftFiles, setDraftFiles] = useState<File[]>([]);
  /** note_id currently accepting a file, so only that row shows a spinner. */
  const [attachingNoteId, setAttachingNoteId] = useState<number | null>(null);
  /** attachment_id currently being removed, so only that chip shows a spinner. */
  const [deletingAttachmentId, setDeletingAttachmentId] = useState<number | null>(null);
  const [draft, setDraft] = useState('');
  const [draftSubject, setDraftSubject] = useState('');
  const [draftCategories, setDraftCategories] = useState<number[]>([]);
  const [saving, setSaving] = useState(false);

  const [editingNoteId, setEditingNoteId] = useState<number | null>(null);
  const [editText, setEditText] = useState('');
  const [editCategories, setEditCategories] = useState<number[]>([]);
  // A logged activity is edited in place too. Activities could previously only be
  // created and completed, so a case manager who needed to add detail to a follow-up
  // she had already logged had nowhere to put it (TRUS-509).
  const [editingActivityId, setEditingActivityId] = useState<number | null>(null);

  const [managerOpen, setManagerOpen] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  // Server's answer, not a client-side role guess: authorization is on the ORG
  // role, which the browser does not reliably know.
  const [canManage, setCanManage] = useState(false);
  const [hasMore, setHasMore] = useState(false);

  // --- data ----------------------------------------------------------------
  const loadCategories = useCallback(async () => {
    try {
      const res = await memberActivityFeedAPI.listCategories();
      setCategories(res.items || []);
      setCanManage(Boolean(res.can_manage));
    } catch {
      // Non-fatal: the feed is still readable without the tag picker.
      setCategories([]);
      setCanManage(false);
    }
  }, []);

  const loadFeed = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await memberActivityFeedAPI.getFeed(memberId, {
        categoryId: categoryFilter,
        origin: originFilter,
        limit,
      });
      setItems(res.items || []);
      setHasMore(Boolean(res.has_more));
    } catch (e: any) {
      setError(getApiErrorMessage(e) || t('activity.loadFailed', 'Could not load this member’s activity.'));
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [memberId, categoryFilter, originFilter, limit, t]);

  useEffect(() => { loadCategories(); }, [loadCategories]);
  useEffect(() => { loadFeed(); }, [loadFeed]);

  const activeCategories = useMemo(() => categories.filter((c) => c.is_active), [categories]);

  // --- actions -------------------------------------------------------------
  const toggle = (list: number[], id: number) =>
    list.includes(id) ? list.filter((x) => x !== id) : [...list, id];

  const saveNote = async () => {
    if (!draft.trim()) return;
    setSaving(true);
    try {
      const created = await memberActivityFeedAPI.createNote(memberId, {
        note_text: draft.trim(),
        subject: draftSubject.trim() || undefined,
        enrollment_id: enrollmentId,
        category_ids: draftCategories,
      });
      // Sequential, not Promise.all: a partial failure should leave the files that
      // did upload attached, and the note itself is already saved either way.
      if (draftFiles.length && created?.id) {
        for (const file of draftFiles) {
          await memberActivityFeedAPI.uploadNoteAttachment(created.id, file);
        }
      }
      setDraft(''); setDraftSubject(''); setDraftCategories([]); setDraftFiles([]); setComposerOpen(false);
      await loadFeed();
    } catch (e: any) {
      setError(getApiErrorMessage(e) || t('activity.saveFailed', 'Could not save the note.'));
    } finally {
      setSaving(false);
    }
  };

  const beginEdit = (item: ActivityFeedItem) => {
    setEditingNoteId(item.meta?.note_id ?? null);
    setEditText(item.body || '');
    setEditCategories((item.categories || []).map((c) => c.id));
  };

  const saveEdit = async () => {
    if (editingNoteId == null) return;
    setSaving(true);
    try {
      // Sends category_ids explicitly so clearing every tag actually clears them —
      // which is what drops a note out of the Recovery Plan.
      await memberActivityFeedAPI.updateNote(editingNoteId, {
        note_text: editText,
        category_ids: editCategories,
      });
      setEditingNoteId(null);
      await loadFeed();
    } catch (e: any) {
      setError(getApiErrorMessage(e) || t('activity.saveFailed', 'Could not save the note.'));
    } finally {
      setSaving(false);
    }
  };

  const attachToNote = async (noteId: number, files: FileList | null) => {
    if (!files || files.length === 0) return;
    setAttachingNoteId(noteId);
    setError(null);
    try {
      for (const file of Array.from(files)) {
        await memberActivityFeedAPI.uploadNoteAttachment(noteId, file);
      }
      await loadFeed();
    } catch (e: any) {
      setError(getApiErrorMessage(e) || t('activity.attachFailed', 'Could not attach that file.'));
    } finally {
      setAttachingNoteId(null);
    }
  };

  // Remove a file already hung off a note — the counterpart to attachToNote. A DCM
  // arranging notes by need has to be able to take a file off the wrong note before
  // attaching it to the right one (TRUS-541). Author-or-admin only, same as editing
  // the note; the backend keeps the stored object, so this only drops the link.
  const removeAttachment = async (noteId: number, attachment: NoteAttachment) => {
    if (!window.confirm(
      t('activity.removeAttachmentConfirm', 'Remove “{name}” from this note?')
        .replace('{name}', attachment.filename),
    )) return;
    setDeletingAttachmentId(attachment.id);
    setError(null);
    try {
      await memberActivityFeedAPI.deleteNoteAttachment(noteId, attachment.id);
      await loadFeed();
    } catch (e: any) {
      setError(getApiErrorMessage(e) || t('activity.removeAttachmentFailed', 'Could not remove that file.'));
    } finally {
      setDeletingAttachmentId(null);
    }
  };

  /** Streamed through the backend, so it needs the auth header — not a plain link. */
  const downloadAttachment = async (noteId: number, attachment: NoteAttachment) => {
    try {
      const blob = await memberActivityFeedAPI.downloadNoteAttachment(noteId, attachment.id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = attachment.filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e: any) {
      setError(getApiErrorMessage(e) || t('activity.downloadFailed', 'Could not open that attachment.'));
    }
  };

  const beginEditActivity = (item: ActivityFeedItem) => {
    setEditingActivityId(activityIdOf(item));
    setEditText(item.body || '');
  };

  const saveActivityEdit = async () => {
    if (editingActivityId == null) return;
    setSaving(true);
    try {
      await enrollmentsAPI.updateActivity(editingActivityId, { completion_notes: editText });
      setEditingActivityId(null);
      await loadFeed();
    } catch (e: any) {
      setError(getApiErrorMessage(e) || t('activity.saveFailed', 'Could not save the activity.'));
    } finally {
      setSaving(false);
    }
  };

  const addCategory = async () => {
    const name = newCategoryName.trim();
    if (!name) return;
    try {
      await memberActivityFeedAPI.createCategory(name);
      setNewCategoryName('');
      await loadCategories();
    } catch (e: any) {
      setError(getApiErrorMessage(e) || t('activity.categoryFailed', 'Could not add that category.'));
    }
  };

  const removeCategory = async (id: number) => {
    try {
      await memberActivityFeedAPI.deleteCategory(id);
      await loadCategories();
      if (categoryFilter === id) setCategoryFilter(null);
    } catch (e: any) {
      setError(getApiErrorMessage(e) || t('activity.categoryFailed', 'Could not remove that category.'));
    }
  };

  // --- render --------------------------------------------------------------
  const chip = (c: NoteCategory) => (
    <span
      key={c.id}
      className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-medium border"
      style={{
        color: c.color || undefined,
        borderColor: c.color ? `${c.color}55` : undefined,
        backgroundColor: c.color ? `${c.color}14` : undefined,
      }}
    >
      <Tag className="w-2.5 h-2.5" />
      {c.name}
    </span>
  );

  return (
    <div className="space-y-4">
      {/* Toolbar: filter by category, split notes vs system, add a note */}
      <div className="flex flex-wrap items-center gap-2">
        <Filter className="w-4 h-4 text-gray-400" />
        <button
          type="button"
          onClick={() => setCategoryFilter(null)}
          className={`px-2.5 py-1 text-xs rounded-md border transition-colors ${
            categoryFilter === null
              ? 'bg-v2accent text-white border-v2accent'
              : 'border-v2border bg-surface text-ink-500 hover:text-ink'
          }`}
        >
          {t('activity.allCategories', 'All')}
        </button>
        {activeCategories.map((c) => (
          <button
            key={c.id}
            type="button"
            onClick={() => {
              const next = categoryFilter === c.id ? null : c.id;
              setCategoryFilter(next);
              // A category exists only on notes; leaving origin=system set would
              // return nothing, and the disabled control could not undo it.
              if (next !== null) setOriginFilter(null);
            }}
            className={`px-2.5 py-1 text-xs rounded-md border transition-colors ${
              categoryFilter === c.id
                ? 'bg-v2accent text-white border-v2accent'
                : 'border-v2border bg-surface text-ink-500 hover:text-ink'
            }`}
          >
            {c.name}
          </button>
        ))}

        <span className="mx-1 h-4 w-px bg-gray-300 dark:bg-gray-600" />

        {/* Notes vs system. Disabled while a category filter is on, because a
            category only exists on notes — the combination has no meaning. */}
        <select
          value={originFilter ?? ''}
          onChange={(e) => setOriginFilter((e.target.value || null) as FeedOrigin | null)}
          disabled={categoryFilter !== null}
          className="text-xs rounded-md border border-v2border bg-surface px-2 py-1 disabled:opacity-50"
          aria-label={t('activity.filterOrigin', 'Filter by source')}
        >
          <option value="">{t('activity.everything', 'Everything')}</option>
          <option value="note">{t('activity.notesOnly', 'Notes only')}</option>
          <option value="system">{t('activity.updatesOnly', 'Updates only')}</option>
        </select>

        <div className="ml-auto flex items-center gap-2">
          {canManage && (
            <Button size="sm" variant="outline" onClick={() => setManagerOpen((v) => !v)}>
              <Settings2 className="w-4 h-4 mr-1.5" />
              {t('activity.manageCategories', 'Categories')}
            </Button>
          )}
          {!readOnly && (
            <Button size="sm" onClick={() => setComposerOpen((v) => !v)}>
              <Plus className="w-4 h-4 mr-1.5" />
              {t('activity.addNote', 'Add note')}
            </Button>
          )}
        </div>
      </div>

      {/* Admin: edit the org's own category list */}
      {managerOpen && canManage && (
        <div className="rounded-lg border border-v2border bg-surface p-3 space-y-2">
          <p className="text-xs text-gray-500">
            {t('activity.categoriesHelp', 'These categories belong to your organization. Removing one hides it from the picker; notes already tagged keep the tag.')}
          </p>
          <div className="flex flex-wrap gap-2">
            {categories.filter((c) => c.is_active).map((c) => (
              <span key={c.id} className="inline-flex items-center gap-1.5 rounded border border-v2border px-2 py-1 text-xs">
                {c.name}
                {typeof c.note_count === 'number' && c.note_count > 0 && (
                  <span className="text-gray-400">({c.note_count})</span>
                )}
                <button
                  type="button"
                  onClick={() => removeCategory(c.id)}
                  aria-label={`${t('activity.removeCategory', 'Remove category')}: ${c.name}`}
                  className="text-gray-400 hover:text-red-600"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
          <div className="flex items-center gap-2 pt-1">
            <input
              value={newCategoryName}
              onChange={(e) => setNewCategoryName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') addCategory(); }}
              placeholder={t('activity.newCategory', 'New category name')}
              className="flex-1 max-w-xs text-sm rounded-md border border-v2border bg-surface px-2 py-1.5"
            />
            <Button size="sm" variant="outline" onClick={addCategory} disabled={!newCategoryName.trim()}>
              {t('common.add', 'Add')}
            </Button>
          </div>
        </div>
      )}

      {/* Composer */}
      {composerOpen && !readOnly && (
        <div className="rounded-lg border border-v2border bg-surface p-3 space-y-2">
          <input
            value={draftSubject}
            onChange={(e) => setDraftSubject(e.target.value)}
            placeholder={t('activity.subjectOptional', 'Subject (optional)')}
            className="w-full text-sm rounded-md border border-v2border bg-surface px-2 py-1.5"
          />
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={5}
            placeholder={t('activity.notePlaceholder', 'What happened?')}
            className="w-full text-sm rounded-md border border-v2border bg-surface px-2 py-1.5 resize-y min-h-[120px] leading-relaxed"
          />
          {activeCategories.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {activeCategories.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => setDraftCategories((prev) => toggle(prev, c.id))}
                  aria-pressed={draftCategories.includes(c.id)}
                  className={`px-2 py-1 text-xs rounded border transition-colors ${
                    draftCategories.includes(c.id)
                      ? 'bg-v2accent text-white border-v2accent'
                      : 'border-v2border text-ink-500 hover:text-ink'
                  }`}
                >
                  {c.name}
                </button>
              ))}
            </div>
          )}
          {/* No name, no category — the file is simply what the note is about
              (TRUS-520). They upload once the note itself exists. */}
          <div className="flex flex-wrap items-center gap-2">
            <label className="inline-flex items-center gap-1.5 text-xs text-ink-500 hover:text-ink cursor-pointer">
              <Paperclip className="w-3.5 h-3.5" />
              {t('activity.attachFile', 'Attach a file')}
              <input
                type="file"
                multiple
                className="hidden"
                onChange={(e) => {
                  setDraftFiles((prev) => [...prev, ...Array.from(e.target.files || [])]);
                  // Clear the input so re-picking the same file still fires onChange.
                  e.target.value = '';
                }}
              />
            </label>
            {draftFiles.map((f, i) => (
              <span
                key={`${f.name}-${i}`}
                className="inline-flex items-center gap-1 rounded border border-v2border px-2 py-0.5 text-xs text-ink-500"
              >
                {f.name}
                <button
                  type="button"
                  aria-label={t('activity.removeFile', 'Remove file')}
                  onClick={() => setDraftFiles((prev) => prev.filter((_, j) => j !== i))}
                  className="hover:text-red-600"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
            {draftFiles.length > 0 && (
              // Staged, not uploaded. Without this the chip looks like a finished
              // upload, so closing the composer silently loses the file.
              <span className="text-[11px] text-ink-400">
                {t('activity.attachesOnSave', 'attaches when you save the note')}
              </span>
            )}
          </div>
          {/* A picked file is only STAGED — it uploads when the note is saved, because
              the attachment hangs off a note_id that does not exist yet. With no note
              text the Save button is disabled (the API requires note_text), so a file
              picked on its own sits there with nothing visibly happening and reads as
              "I was unable to attach it" — which is exactly how TRUS-553 was reported,
              by a user whose file had in fact attached fine once she typed something.
              Say why the button is dead instead of leaving her to guess. */}
          {draftFiles.length > 0 && !draft.trim() && (
            <p className="text-xs text-amber-600 dark:text-amber-500">
              {t(
                'activity.fileNeedsNote',
                'Add a note before saving — the file attaches to the note, so it cannot be saved on its own.',
              )}
            </p>
          )}
          <div className="flex items-center gap-2">
            <Button size="sm" onClick={saveNote} disabled={saving || !draft.trim()}>
              {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}
              {t('activity.saveNote', 'Save note')}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setComposerOpen(false)}>
              {t('common.cancel', 'Cancel')}
            </Button>
          </div>
        </div>
      )}

      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-950/30 dark:border-red-900 dark:text-red-300">
          {error}
        </div>
      )}

      {/* The feed */}
      {loading ? (
        <div className="flex items-center justify-center gap-2 py-10 text-gray-400">
          <Loader2 className="w-4 h-4 animate-spin" /> {t('activity.loading', 'Loading activity…')}
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-10 text-gray-400">
          <p className="text-sm">{t('activity.empty', 'Nothing on this member yet.')}</p>
          {categoryFilter !== null && (
            <button type="button" className="text-xs underline mt-1" onClick={() => setCategoryFilter(null)}>
              {t('activity.clearFilter', 'Clear the category filter')}
            </button>
          )}
        </div>
      ) : (
        <ol className="space-y-2">
          {items.map((item) => {
            const style = KIND_STYLE[item.kind] || KIND_STYLE.note;
            const Icon = style.icon;
            const isNote = item.origin === 'note';
            const editing = isNote && editingNoteId === item.meta?.note_id;
            const noteId: number | null = isNote ? item.meta?.note_id ?? null : null;
            const attachments: NoteAttachment[] = (item.meta?.attachments as NoteAttachment[]) || [];
            const activityId = activityIdOf(item);
            const editingActivity = activityId !== null && editingActivityId === activityId;
            return (
              <li
                key={item.id}
                className={`relative rounded-lg border pl-4 pr-3 py-3 ${
                  isNote
                    ? 'border-v2border bg-surface'
                    : 'border-dashed border-gray-200 dark:border-gray-700 bg-gray-50/60 dark:bg-gray-900/30'
                }`}
              >
                {/* Left rail: the at-a-glance note-vs-system signal */}
                <span className={`absolute left-0 top-0 bottom-0 w-1 rounded-l-lg ${style.rail}`} aria-hidden="true" />

                <div className="flex items-start gap-2">
                  <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${isNote ? 'text-v2accent' : 'text-gray-400'}`} />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                      <span className="text-sm font-medium">
                        {item.title || (isNote ? t('activity.note', 'Note') : style.label)}
                      </span>
                      {!isNote && (
                        <Badge variant="secondary" className="text-[10px] uppercase tracking-wide">
                          {style.label}
                        </Badge>
                      )}
                      <span className="text-xs text-gray-400 inline-flex items-center gap-1">
                        <User className="w-3 h-3" />
                        {item.author_name || t('activity.system', 'System')}
                      </span>
                      <span className="text-xs text-gray-400">
                        {item.occurred_at ? fmt.dateTime(item.occurred_at) : ''}
                      </span>
                      {item.meta?.edited && (
                        <span className="text-xs text-gray-400 italic">{t('activity.edited', 'edited')}</span>
                      )}
                    </div>

                    {/* The whole point: the note body, in full, no truncation. */}
                    {editing ? (
                      <div className="mt-2 space-y-2">
                        <textarea
                          value={editText}
                          onChange={(e) => setEditText(e.target.value)}
                          rows={5}
                          className="w-full text-sm rounded-md border border-v2border bg-surface px-2 py-1.5 resize-y min-h-[120px] leading-relaxed"
                        />
                        <div className="flex flex-wrap gap-1.5">
                          {activeCategories.map((c) => (
                            <button
                              key={c.id}
                              type="button"
                              onClick={() => setEditCategories((prev) => toggle(prev, c.id))}
                              aria-pressed={editCategories.includes(c.id)}
                              className={`px-2 py-1 text-xs rounded border ${
                                editCategories.includes(c.id)
                                  ? 'bg-v2accent text-white border-v2accent'
                                  : 'border-v2border text-ink-500'
                              }`}
                            >
                              {c.name}
                            </button>
                          ))}
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" onClick={saveEdit} disabled={saving}>
                            {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}
                            {t('common.save', 'Save')}
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => setEditingNoteId(null)}>
                            <X className="w-4 h-4 mr-1.5" />{t('common.cancel', 'Cancel')}
                          </Button>
                        </div>
                      </div>
                    ) : editingActivity ? (
                      <div className="mt-2 space-y-2">
                        <textarea
                          value={editText}
                          onChange={(e) => setEditText(e.target.value)}
                          rows={5}
                          className="w-full text-sm rounded-md border border-v2border bg-surface px-2 py-1.5 resize-y min-h-[120px] leading-relaxed"
                          placeholder={t('activity.activityNotes', 'Notes on this activity')}
                        />
                        <div className="flex gap-2">
                          <Button size="sm" onClick={saveActivityEdit} disabled={saving}>
                            {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}
                            {t('common.save', 'Save')}
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => setEditingActivityId(null)}>
                            <X className="w-4 h-4 mr-1.5" />{t('common.cancel', 'Cancel')}
                          </Button>
                        </div>
                      </div>
                    ) : (
                      item.body && (
                        <p className="mt-1.5 text-sm text-ink whitespace-pre-wrap leading-relaxed break-words">
                          {item.body}
                        </p>
                      )
                    )}

                    {/* Files hung off the note. Rendered as download chips rather than
                        plain links: the stream needs the auth header (TRUS-520). */}
                    {(attachments.length > 0 || (isNote && item.editable && !readOnly)) && (
                      <div className="mt-2 flex flex-wrap items-center gap-1.5">
                        {attachments.map((a) => {
                          const canDelete = isNote && item.editable && !readOnly && noteId !== null;
                          return (
                            <span
                              key={a.id}
                              className="inline-flex items-center rounded border border-v2border text-xs text-ink-500 max-w-[264px] overflow-hidden"
                            >
                              <button
                                type="button"
                                onClick={() => noteId !== null && downloadAttachment(noteId, a)}
                                title={a.filename}
                                className="inline-flex items-center gap-1 px-2 py-0.5 hover:text-v2accent max-w-[240px]"
                              >
                                <Download className="w-3 h-3 shrink-0" />
                                <span className="truncate">{a.filename}</span>
                              </button>
                              {canDelete && (
                                <button
                                  type="button"
                                  onClick={() => removeAttachment(noteId as number, a)}
                                  disabled={deletingAttachmentId === a.id}
                                  aria-label={`${t('activity.removeFile', 'Remove file')}: ${a.filename}`}
                                  title={t('activity.removeFile', 'Remove file')}
                                  className="flex items-center border-l border-v2border px-1 py-0.5 text-gray-400 hover:text-red-600 disabled:opacity-50"
                                >
                                  {deletingAttachmentId === a.id ? (
                                    <Loader2 className="w-3 h-3 animate-spin" />
                                  ) : (
                                    <X className="w-3 h-3" />
                                  )}
                                </button>
                              )}
                            </span>
                          );
                        })}
                        {isNote && item.editable && !readOnly && noteId !== null && (
                          <label className="inline-flex items-center gap-1 text-[11px] text-gray-400 hover:text-v2accent cursor-pointer">
                            {attachingNoteId === noteId ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              <Paperclip className="w-3 h-3" />
                            )}
                            {t('activity.attachFile', 'Attach a file')}
                            <input
                              type="file"
                              multiple
                              className="hidden"
                              disabled={attachingNoteId === noteId}
                              onChange={(e) => {
                                void attachToNote(noteId, e.target.files);
                                e.target.value = '';
                              }}
                            />
                          </label>
                        )}
                      </div>
                    )}

                    {(item.categories?.length > 0 ||
                      (isNote && item.editable && !editing) ||
                      (activityId !== null && item.editable && !editingActivity)) && (
                      <div className="mt-2 flex flex-wrap items-center gap-1.5">
                        {item.categories?.map(chip)}
                        {isNote && item.editable && !editing && !readOnly && (
                          <button
                            type="button"
                            onClick={() => beginEdit(item)}
                            className="text-[11px] text-gray-400 hover:text-v2accent underline ml-1"
                          >
                            {t('common.edit', 'Edit')}
                          </button>
                        )}
                        {/* Same affordance on a logged activity the caller may correct —
                            the backend marks it editable for the author and for admins,
                            and never once it has been billed (TRUS-509). */}
                        {activityId !== null && item.editable && !editingActivity && !readOnly && (
                          <button
                            type="button"
                            onClick={() => beginEditActivity(item)}
                            className="text-[11px] text-gray-400 hover:text-v2accent underline ml-1"
                          >
                            {t('common.edit', 'Edit')}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ol>
      )}

      {/* Paginate on has_more. The API caps limit at 200, so stop offering the
          control at the ceiling rather than sending 250 and 422-ing the feed. */}
      {!loading && items.length > 0 && hasMore && limit < MAX_FEED_LIMIT && (
        <div className="flex justify-center">
          <Button
            size="sm"
            variant="outline"
            onClick={() => setLimit((l) => Math.min(l + PAGE_SIZE, MAX_FEED_LIMIT))}
          >
            {t('activity.loadMore', 'Show older')}
          </Button>
        </div>
      )}
    </div>
  );
}
